import React, { useMemo } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import FantasyDriverDetailsDrawer from "./FantasyDriverDetailsDrawer";
import {
  getAverageFeatureValue,
  getFeatureValue,
  getSeasonLabel,
} from "../utils/raceUtils";
import { useDriverCarNumbers } from "../hooks/useDriverCarNumbers";
import { useCurrentSeason } from "../hooks/useCurrentSeason";
import { useDriverManufacturers } from "../hooks/useDriverManufacturers";
import { useFantasyDrawer } from "./hooks/useFantasyDrawer";
import { useSortedDrivers } from "../hooks/useSortedDrivers";
import { tableContainerSx } from "../styles/tableStyles";
import { stickyOnPhone, phoneNumberColumn, PHONE_NUMBER_COLUMN } from "../styles/responsive";
import ManufacturerLogo from "./ManufacturerLogo";
import { getRaceYear } from "../utils/raceDate";

const FantasyDriverTable = ({
  groupDrivers,
  trackRaces,
  similarTrackRaces,
  allRaces,
  currentSeasonRaces,
  pastSeasonRaces,
  feature,
  name,
}) => {
  const excludePlayoffs = false;
  const [excludeDnf, setExcludeDnf] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("track");

  const { selectedDriver, handleDriverClick, closeDrawer } = useFantasyDrawer();
  const driverCarNumbers = useDriverCarNumbers(allRaces || currentSeasonRaces);
  const driverManufacturers = useDriverManufacturers(allRaces || currentSeasonRaces);

  // Filter trackRaces to only show past 3 seasons (the season the data is in + the previous 2)
  const currentYear = useCurrentSeason();
  const filteredTrackRaces = useMemo(() => {
    const minYear = currentYear - 2; // Current season and 2 back = 3 seasons total

    return trackRaces.filter((race) => {
      const raceYear = race.season_year || getRaceYear(race.race_date);
      return raceYear >= minYear && raceYear <= currentYear;
    });
  }, [trackRaces, currentYear]);

  const trackRaceDates = [
    ...new Set(filteredTrackRaces.map((race) => race.race_date)),
  ].sort((a, b) => new Date(b) - new Date(a));

  const currentSeasonRaceNumbers = [
    ...new Set(currentSeasonRaces.map((r) => r.race_number)),
  ].sort((a, b) => b - a);
  const last5RacesForTable = currentSeasonRaces.filter((r) =>
    currentSeasonRaceNumbers.slice(0, 5).includes(r.race_number)
  );
  const last10Races = currentSeasonRaces.filter((r) =>
    currentSeasonRaceNumbers.slice(0, 10).includes(r.race_number)
  );

  const currentSeasonYear = currentSeasonRaces[0]?.season_year || new Date().getFullYear();
  const typeSeasonRaces = similarTrackRaces.filter(r => {
    const raceYear = r.season_year || getRaceYear(r.race_date);
    return raceYear === currentSeasonYear;
  });

  const sortRaces = useMemo(() => {
    switch (sortBy) {
      case "type": return similarTrackRaces;
      case "typeSeason": return typeSeasonRaces;
      case "l5": return last5RacesForTable;
      case "l10": return last10Races;
      case "track":
      default: return trackRaces;
    }
  }, [sortBy, trackRaces, similarTrackRaces, typeSeasonRaces, last5RacesForTable, last10Races]);

  const sortedDrivers = useSortedDrivers(
    groupDrivers,
    sortRaces,
    feature,
    excludePlayoffs,
    excludeDnf
  );

  // Pre-calculate all race cell values to avoid filtering on every render
  const raceValuesByDriver = useMemo(() => {
    const results = {};
    sortedDrivers.forEach(driver => {
      results[driver] = {};
      trackRaceDates.forEach(race_date => {
        const currentRace = filteredTrackRaces.filter(
          (race) => race.race_date === race_date
        );
        results[driver][race_date] = getFeatureValue(
          currentRace,
          driver,
          excludePlayoffs,
          excludeDnf,
          feature
        );
      });
    });
    return results;
  }, [sortedDrivers, trackRaceDates, filteredTrackRaces, excludePlayoffs, excludeDnf, feature]);

  return (
    <Paper sx={{ borderRadius: 3, p: 3, boxShadow: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          {feature !== "quali_pos" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeDnf}
                  onChange={() => setExcludeDnf(!excludeDnf)}
                />
              }
              label="Exclude DNFs"
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "rgba(255, 0, 0, 0.2)",
              border: "1px solid red",
              borderRadius: 1,
              mr: 1,
            }}
          />
          <Typography variant="body2">Did Not Finish (DNF)</Typography>
        </Box>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
              }}
            >
              <TableCell rowSpan={2} sx={[{ fontWeight: "bold", px: 2 }, phoneNumberColumn, stickyOnPhone(0, { header: true })]}>
                #
              </TableCell>
              <TableCell rowSpan={2} sx={[{ fontWeight: "bold", px: 2 }, stickyOnPhone(PHONE_NUMBER_COLUMN, { header: true })]}>
                Driver
              </TableCell>
              <TableCell
                colSpan={5}
                sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? '#4F4F4F' : 'grey.300',
                }}
              >
                Average {name}
              </TableCell>
              {trackRaceDates.map((race_date, index) => (
                <TableCell
                  key={index}
                  rowSpan={2}
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  {getSeasonLabel(race_date)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
              }}
            >
              <Tooltip title={`Average ${name.toLowerCase()} specifically at this track (next race venue)`}>
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: (theme) => sortBy === "track" 
                    ? (theme.palette.mode === 'dark' ? '#5E5E5E' : 'grey.400')
                    : (theme.palette.mode === 'dark' ? '#6E6E6E' : 'grey.200'),
                }} onClick={() => setSortBy("track")}>
                  At Track
                </TableCell>
              </Tooltip>
              <Tooltip
                title={`Average ${name.toLowerCase()} on similar track types (superspeedways, short tracks, etc.)`}
              >
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: (theme) => sortBy === "type" 
                    ? (theme.palette.mode === 'dark' ? '#5E5E5E' : 'grey.400')
                    : (theme.palette.mode === 'dark' ? '#6E6E6E' : 'grey.200'),
                }} onClick={() => setSortBy("type")}>
                  Track Type
                </TableCell>
              </Tooltip>
              <Tooltip
                title={`Average ${name.toLowerCase()} on similar track types this season`}
              >
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: (theme) => sortBy === "typeSeason" 
                    ? (theme.palette.mode === 'dark' ? '#5E5E5E' : 'grey.400')
                    : (theme.palette.mode === 'dark' ? '#6E6E6E' : 'grey.200'),
                }} onClick={() => setSortBy("typeSeason")}>
                  Type '{currentSeasonYear.toString().slice(-2)}
                </TableCell>
              </Tooltip>
              <Tooltip
                title={`Recent form - Average ${name.toLowerCase()} in the past 5 races on all tracks`}
              >
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: (theme) => sortBy === "l5" 
                    ? (theme.palette.mode === 'dark' ? '#5E5E5E' : 'grey.400')
                    : (theme.palette.mode === 'dark' ? '#6E6E6E' : 'grey.200'),
                }} onClick={() => setSortBy("l5")}>
                  L5 Form
                </TableCell>
              </Tooltip>
              <Tooltip
                title={`Recent momentum - Average ${name.toLowerCase()} in the past 10 races on all tracks`}
              >
                <TableCell sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: (theme) => sortBy === "l10" 
                    ? (theme.palette.mode === 'dark' ? '#5E5E5E' : 'grey.400')
                    : (theme.palette.mode === 'dark' ? '#6E6E6E' : 'grey.200'),
                }} onClick={() => setSortBy("l10")}>
                  L10 Form
                </TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedDrivers.map((driver, index) => (
              <TableRow
                key={index}
                sx={{
                  bgcolor:
                    index % 2 === 0 ? "background.default" : "action.hover",
                  "&:hover": {
                    bgcolor: "action.selected",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <TableCell
                  sx={[{
                    width: "40px",
                    fontWeight: "bold",
                    px: 2,
                    cursor: "pointer",
                  }, phoneNumberColumn, stickyOnPhone(0, { odd: index % 2 === 1 })]}
                >
                  {driverCarNumbers[driver] || "-"}
                </TableCell>
                <TableCell
                  sx={[{ fontWeight: "bold", px: 2, cursor: "pointer" }, stickyOnPhone(PHONE_NUMBER_COLUMN, { odd: index % 2 === 1 })]}
                  onClick={() => handleDriverClick(driver)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ManufacturerLogo manufacturer={driverManufacturers[driver]} />
                    {driver}
                  </Box>
                </TableCell>
                <Tooltip title={`${driver}'s average ${name.toLowerCase()} specifically at this track`}>
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(
                      trackRaces,
                      driver,
                      excludePlayoffs,
                      excludeDnf,
                      feature
                    )}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={`${driver}'s average ${name.toLowerCase()} on similar track types`}
                >
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(
                      similarTrackRaces,
                      driver,
                      excludePlayoffs,
                      excludeDnf,
                      feature
                    )}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={`${driver}'s average ${name.toLowerCase()} on similar track types this season`}
                >
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(
                      typeSeasonRaces,
                      driver,
                      excludePlayoffs,
                      excludeDnf,
                      feature
                    )}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={`${driver}'s recent form - average ${name.toLowerCase()} in last 5 races on all tracks`}
                >
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(
                      last5RacesForTable,
                      driver,
                      excludePlayoffs,
                      excludeDnf,
                      feature
                    )}
                  </TableCell>
                </Tooltip>
                <Tooltip
                  title={`${driver}'s momentum - average ${name.toLowerCase()} in last 10 races on all tracks`}
                >
                  <TableCell sx={{
                    textAlign: "center",
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                      ? index % 2 === 0 ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.05)'
                      : index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                  }}>
                    {getAverageFeatureValue(
                      last10Races,
                      driver,
                      excludePlayoffs,
                      excludeDnf,
                      feature
                    )}
                  </TableCell>
                </Tooltip>
                {trackRaceDates.map((race_date, idx) => {
                  // Use pre-calculated values instead of recalculating on every render
                  const { value, status } = raceValuesByDriver[driver][race_date];
                  return (
                    <Tooltip title={`${name} in this race`} key={idx}>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          bgcolor:
                            status !== "finished"
                              ? "rgba(255, 0, 0, 0.2)"
                              : "inherit",
                          fontWeight: status !== "finished" ? "bold" : "normal",
                        }}
                      >
                        {value}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedDriver && (
        <FantasyDriverDetailsDrawer
          driver={selectedDriver}
          groupDrivers={groupDrivers}
          trackRaces={trackRaces}
          similarTrackRaces={similarTrackRaces}
          allRaces={allRaces}
          pastSeasonRaces={pastSeasonRaces}
          currentSeasonRaces={currentSeasonRaces}
          onClose={closeDrawer}
        />
      )}
    </Paper>
  );
};

export default FantasyDriverTable;
